import { 
  collection, 
  query, 
  where, 
  getDocs,
  orderBy,
  startAfter,
  limit,
  Timestamp
} from 'firebase/firestore';
import { db } from './firebase';

const COLLECTION_NAME = 'points_history';

// Cache para clientes já buscados para evitar consultas desnecessárias
const customersCache = new Map();

// Função auxiliar para buscar dados do cliente com cache
async function getCustomerData(customerId) {
  // Verificar cache primeiro
  if (customersCache.has(customerId)) {
    return customersCache.get(customerId);
  }

  try {
    const { customerService } = await import('./customerService');
    const customerData = await customerService.getById(customerId);
    
    // Armazenar no cache
    const result = {
      name: customerData?.name || 'Cliente não encontrado',
      phone: customerData?.phone || ''
    };
    customersCache.set(customerId, result);
    return result;
  } catch (error) {
    // Cliente pode ter sido excluído mas ainda tem histórico
    const result = {
      name: 'Cliente removido',
      phone: ''
    };
    customersCache.set(customerId, result);
    return result;
  }
}

export const analyticsService = {
  // Limpar cache de clientes (útil quando dados são atualizados)
  clearCustomersCache() {
    customersCache.clear();
  },

  // Obter lista de clientes válidos (que ainda existem no sistema)
  async getValidCustomerIds(companyId) {
    try {
      const { customerService } = await import('./customerService');
      const customers = await customerService.getAll();
      
      return new Set(
        customers
          .filter(customer => customer.companyId === companyId)
          .map(customer => customer.id)
      );
    } catch (error) {
      console.error('Erro ao buscar clientes válidos:', error);
      return new Set();
    }
  },

  // Buscar padrão de consumo por dia da semana e horário
  async getConsumptionPatterns(companyId, daysBack = 30) {
    try {
      if (!companyId) {
        throw new Error('ID da empresa é obrigatório');
      }

      // Calcular data inicial (X dias atrás)
      const endDate = new Date();
      const startDate = new Date();
      startDate.setDate(startDate.getDate() - daysBack);

      // Buscar todas as transações de pontos da empresa no período
      const q = query(
        collection(db, COLLECTION_NAME),
        where('companyId', '==', companyId),
        where('createdAt', '>=', Timestamp.fromDate(startDate)),
        where('createdAt', '<=', Timestamp.fromDate(endDate)),
        orderBy('createdAt', 'asc')
      );

      const snapshot = await getDocs(q);
      const allTransactions = [];

      snapshot.forEach((doc) => {
        const data = doc.data();
        allTransactions.push({
          id: doc.id,
          ...data,
          createdAt: data.createdAt?.toDate ? data.createdAt.toDate() : new Date(data.createdAt)
        });
      });

      // Filtrar apenas transações de clientes que ainda existem
      const validTransactions = await this.filterValidCustomerTransactions(allTransactions);

      // Processar dados para extrair padrões
      const patterns = this.processConsumptionData(validTransactions);
      
      return patterns;
    } catch (error) {
      console.error('Erro ao buscar padrões de consumo:', error);
      throw new Error('Erro ao analisar padrões de consumo: ' + error.message);
    }
  },

  // Filtrar transações apenas de clientes que ainda existem
  async filterValidCustomerTransactions(transactions) {
    const { customerService } = await import('./customerService');
    
    // Obter lista única de customerIds
    const uniqueCustomerIds = [...new Set(transactions.map(t => t.customerId))];
    const validCustomerIds = new Set();

    // Verificar em lotes para melhor performance
    const batchSize = 10;
    for (let i = 0; i < uniqueCustomerIds.length; i += batchSize) {
      const batch = uniqueCustomerIds.slice(i, i + batchSize);
      
      await Promise.all(
        batch.map(async (customerId) => {
          try {
            await customerService.getById(customerId);
            validCustomerIds.add(customerId);
          } catch (error) {
            // Cliente não existe mais, não adicionar ao conjunto válido
          }
        })
      );
    }

    // Filtrar transações apenas de clientes válidos
    return transactions.filter(transaction => 
      validCustomerIds.has(transaction.customerId)
    );
  },

  // Processar dados de transações para extrair padrões
  processConsumptionData(transactions) {
    const dayNames = ['Domingo', 'Segunda', 'Terça', 'Quarta', 'Quinta', 'Sexta', 'Sábado'];
    
    // Inicializar estruturas de dados
    const patterns = {
      byDayOfWeek: {},
      byHour: {},
      byDayAndHour: {},
      summary: {
        totalTransactions: transactions.length,
        totalPoints: 0,
        averagePointsPerTransaction: 0,
        mostActiveDay: '',
        mostActiveHour: '',
        peakDayHour: ''
      }
    };

    // Inicializar contadores
    dayNames.forEach(day => {
      patterns.byDayOfWeek[day] = {
        count: 0,
        totalPoints: 0,
        averagePoints: 0
      };
    });

    for (let hour = 0; hour < 24; hour++) {
      patterns.byHour[hour] = {
        count: 0,
        totalPoints: 0,
        averagePoints: 0,
        label: `${hour.toString().padStart(2, '0')}:00`
      };
    }

    // Processar cada transação
    transactions.forEach(transaction => {
      const date = transaction.createdAt;
      const dayOfWeek = dayNames[date.getDay()];
      const hour = date.getHours();
      const points = Math.abs(transaction.points); // Usar valor absoluto

      // Contar por dia da semana
      patterns.byDayOfWeek[dayOfWeek].count++;
      patterns.byDayOfWeek[dayOfWeek].totalPoints += points;

      // Contar por hora
      patterns.byHour[hour].count++;
      patterns.byHour[hour].totalPoints += points;

      // Contar por dia e hora combinados
      const dayHourKey = `${dayOfWeek}-${hour}`;
      if (!patterns.byDayAndHour[dayHourKey]) {
        patterns.byDayAndHour[dayHourKey] = {
          day: dayOfWeek,
          hour,
          count: 0,
          totalPoints: 0
        };
      }
      patterns.byDayAndHour[dayHourKey].count++;
      patterns.byDayAndHour[dayHourKey].totalPoints += points;

      // Somar total de pontos
      patterns.summary.totalPoints += points;
    });

    // Calcular médias
    Object.keys(patterns.byDayOfWeek).forEach(day => {
      const dayData = patterns.byDayOfWeek[day];
      dayData.averagePoints = dayData.count > 0 ? dayData.totalPoints / dayData.count : 0;
    });

    Object.keys(patterns.byHour).forEach(hour => {
      const hourData = patterns.byHour[hour];
      hourData.averagePoints = hourData.count > 0 ? hourData.totalPoints / hourData.count : 0;
    });

    // Calcular estatísticas do resumo
    patterns.summary.averagePointsPerTransaction = 
      patterns.summary.totalTransactions > 0 ? 
      patterns.summary.totalPoints / patterns.summary.totalTransactions : 0;

    // Encontrar dia mais ativo
    let mostActiveDay = '';
    let maxDayCount = 0;
    Object.keys(patterns.byDayOfWeek).forEach(day => {
      if (patterns.byDayOfWeek[day].count > maxDayCount) {
        maxDayCount = patterns.byDayOfWeek[day].count;
        mostActiveDay = day;
      }
    });
    patterns.summary.mostActiveDay = mostActiveDay;

    // Encontrar hora mais ativa
    let mostActiveHour = '';
    let maxHourCount = 0;
    Object.keys(patterns.byHour).forEach(hour => {
      if (patterns.byHour[hour].count > maxHourCount) {
        maxHourCount = patterns.byHour[hour].count;
        mostActiveHour = patterns.byHour[hour].label;
      }
    });
    patterns.summary.mostActiveHour = mostActiveHour;

    // Encontrar combinação dia-hora mais ativa
    let peakDayHour = '';
    let maxCombinedCount = 0;
    Object.keys(patterns.byDayAndHour).forEach(key => {
      const combo = patterns.byDayAndHour[key];
      if (combo.count > maxCombinedCount) {
        maxCombinedCount = combo.count;
        peakDayHour = `${combo.day} às ${combo.hour.toString().padStart(2, '0')}:00`;
      }
    });
    patterns.summary.peakDayHour = peakDayHour;

    return patterns;
  },

  // Buscar dados para gráfico de tendência temporal
  async getTemporalTrends(companyId, daysBack = 30) {
    try {
      if (!companyId) {
        throw new Error('ID da empresa é obrigatório');
      }

      const endDate = new Date();
      const startDate = new Date();
      startDate.setDate(startDate.getDate() - daysBack);

      const q = query(
        collection(db, COLLECTION_NAME),
        where('companyId', '==', companyId),
        where('createdAt', '>=', Timestamp.fromDate(startDate)),
        where('createdAt', '<=', Timestamp.fromDate(endDate)),
        orderBy('createdAt', 'asc')
      );

      const snapshot = await getDocs(q);
      const allTransactions = [];

      snapshot.forEach((doc) => {
        const data = doc.data();
        allTransactions.push({
          id: doc.id,
          ...data,
          createdAt: data.createdAt?.toDate ? data.createdAt.toDate() : new Date(data.createdAt)
        });
      });

      // Filtrar apenas transações de clientes válidos
      const validTransactions = await this.filterValidCustomerTransactions(allTransactions);

      const dailyData = {};

      validTransactions.forEach((data) => {
        const date = data.createdAt;
        const dateKey = date.toISOString().split('T')[0]; // YYYY-MM-DD

        if (!dailyData[dateKey]) {
          dailyData[dateKey] = {
            date: dateKey,
            transactions: 0,
            totalPoints: 0,
            addedPoints: 0,
            redeemedPoints: 0
          };
        }

        dailyData[dateKey].transactions++;
        const points = Math.abs(data.points);
        dailyData[dateKey].totalPoints += points;

        if (data.points > 0) {
          dailyData[dateKey].addedPoints += points;
        } else {
          dailyData[dateKey].redeemedPoints += points;
        }
      });

      // Converter para array e ordenar por data
      const trends = Object.values(dailyData).sort((a, b) => a.date.localeCompare(b.date));
      
      return trends;
    } catch (error) {
      console.error('Erro ao buscar tendências temporais:', error);
      throw new Error('Erro ao analisar tendências: ' + error.message);
    }
  },

  // Buscar estatísticas de clientes mais ativos
  async getTopCustomers(companyId, daysBack = 30, limit = 10) {
    try {
      if (!companyId) {
        throw new Error('ID da empresa é obrigatório');
      }

      const endDate = new Date();
      const startDate = new Date();
      startDate.setDate(startDate.getDate() - daysBack);

      const q = query(
        collection(db, COLLECTION_NAME),
        where('companyId', '==', companyId),
        where('createdAt', '>=', Timestamp.fromDate(startDate)),
        where('createdAt', '<=', Timestamp.fromDate(endDate))
      );

      const snapshot = await getDocs(q);
      const allTransactions = [];

      snapshot.forEach((doc) => {
        const data = doc.data();
        allTransactions.push({
          id: doc.id,
          ...data,
          createdAt: data.createdAt?.toDate ? data.createdAt.toDate() : new Date(data.createdAt)
        });
      });

      // Filtrar apenas transações de clientes válidos
      const validTransactions = await this.filterValidCustomerTransactions(allTransactions);

      const customerStats = {};

      validTransactions.forEach((data) => {
        const customerId = data.customerId;

        if (!customerStats[customerId]) {
          customerStats[customerId] = {
            customerId,
            transactions: 0,
            totalPoints: 0,
            addedPoints: 0,
            redeemedPoints: 0,
            lastTransaction: null
          };
        }

        const stats = customerStats[customerId];
        stats.transactions++;
        
        const points = Math.abs(data.points);
        stats.totalPoints += points;

        if (data.points > 0) {
          stats.addedPoints += points;
        } else {
          stats.redeemedPoints += points;
        }

        const transactionDate = data.createdAt;
        if (!stats.lastTransaction || transactionDate > stats.lastTransaction) {
          stats.lastTransaction = transactionDate;
        }
      });

      // Converter para array e ordenar por número de transações
      const topCustomers = Object.values(customerStats)
        .sort((a, b) => b.transactions - a.transactions)
        .slice(0, limit);

      // Buscar nomes dos clientes (já sabemos que são válidos)
      for (const customer of topCustomers) {
        const customerData = await getCustomerData(customer.customerId);
        customer.name = customerData.name;
        customer.phone = customerData.phone;
      }

      return topCustomers;
    } catch (error) {
      console.error('Erro ao buscar clientes mais ativos:', error);
      throw new Error('Erro ao analisar clientes ativos: ' + error.message);
    }
  }
};
